/**
 * Scan pointers (Z toggle): name + distance tags with edge arrows for
 * whatever the current mode cares about — bodies and the station in
 * flight; resource veins and hostiles on the ground. Entries are keyed by
 * name and FADE in/out (no popping): a target that leaves range, gets
 * eclipsed, or is toggled off eases away gracefully.
 */
import * as THREE from "three";

interface Entry {
  el: HTMLElement;
  nameEl: HTMLElement;
  distEl: HTMLElement;
  arrowEl: HTMLElement;
  chevronEl: HTMLElement;
  alpha: number;
  /** Marked each update; stale entries fade out and are reaped. */
  live: boolean;
  /** Seconds missing — a one-frame occlusion/range flicker at
   *  speed must not dip the fade (reads as a flash). */
  missingT: number;
  lastDist: string;
  lastName: string;
  /** The lane this tag settled in last frame — the screen↔edge decision gets
   *  hysteresis so a subject sitting ON the threshold can't flap between a
   *  centered tag and an edge arrow. */
  lastLane: Lane | null;
  lastEdgeSide: EdgeSide | null;
  /** Displayed position (px): eases toward the de-overlapped slot instead of
   *  snapping to it, so resolving a collision reads as a glide, not a jump. */
  sx: number;
  sy: number;
  /** Sticky label half-width — the max ever measured for this name. The
   *  distance line's character count ticks as the player moves, and letting
   *  the box breathe with it re-packed whole lanes every second. */
  half: number;
}

export interface BodyTarget {
  /** Stable identity for fading (defaults to name). */
  key?: string;
  name: string;
  pos: THREE.Vector3;
  color: string;
  /** Extra line (e.g. "3 LEFT") appended to the distance. */
  sub?: string;
  /** True player→subject distance when `pos` is a display anchor (horizon-
   *  clamped to eye level), not the subject itself. */
  trueDist?: number;
  /** Place names use a dedicated horizon lane: collisions may move them along
   *  the horizon, never vertically away from it. */
  placement?: "world" | "horizon";
}

const FADE_IN_RATE = -Math.log(1 - 0.16) * 60;
const FADE_OUT_RATE = -Math.log(1 - 0.1) * 60;
const MISSING_GRACE_S = 8 / 60;
/** Old 0.2/frame response expressed as a frame-rate-independent time constant. */
const SLOT_EASE_RATE = -Math.log(0.8) * 60;
/** A pair must cross this far before the stable horizon ordering may swap. */
const HORIZON_ORDER_HYSTERESIS = 16;

// --- Label de-overlap ---
/** Tag block heights, px (measured): an edge tag carries an arrow, so it is taller. */
const EDGE_H = 42;
const SCREEN_H = 32;
/**
 * Half-width of a tag, px. The box is as wide as its widest line — the NAME (10px
 * glyphs + 2px letter-spacing ≈ 9.2px/char) or the DISTANCE line (8px + 1px ≈
 * 5.9px/char). Derived from measured labels rather than read back per frame:
 * touching offsetWidth on every tag every frame forces a layout flush, and the
 * distance text changes constantly as the player moves.
 */
function halfWidth(name: string, dist: string): number {
  return Math.max(34, name.length * 4.6, dist.length * 2.95);
}

/** Which screen edge a tag is pinned to — the axis it is free to slide along. */
type Lane = "edge" | "screen" | "horizon";
type EdgeSide = "top" | "bottom" | "left" | "right";

/** One tag's placement for the frame: label centre in screen px. */
interface Slot {
  e: Entry;
  x: number;
  y: number;
  half: number;
  h: number;
  lane: Lane;
  edgeSide?: EdgeSide;
  /** Stable identity — the tiebreaker that keeps lane ordering deterministic. */
  key: string;
}

export class BodyMarkers {
  private root: HTMLElement;
  private entries = new Map<string, Entry>();
  private v = new THREE.Vector3();
  /** This frame's placements, reused (the de-overlap pass runs over them). */
  private slots: Slot[] = [];
  /** Scratch: the slots of the lane currently being spread. */
  private lane: Slot[] = [];
  /** Previous left→right horizon order. It survives small projected crossings,
   *  so a camera tremor cannot exchange two labels' packed slots. */
  private horizonOrder: string[] = [];

  constructor() {
    this.root = document.createElement("div");
    this.root.id = "body-markers";
    document.body.appendChild(this.root);
  }

  private entry(key: string): Entry {
    let e = this.entries.get(key);
    if (!e) {
      const el = document.createElement("div");
      el.className = "pm pm-body";
      el.dataset.markerKey = key;
      // Chevron LAST: its point is the bottom anchor that touches the subject
      // or horizon. The old order put it above the name despite the layout
      // treating it as an underneath pointer.
      el.innerHTML = `<span class="pm-arrow">▲</span><b></b><i></i><span class="pm-chevron">▽</span>`;
      el.style.opacity = "0";
      this.root.appendChild(el);
      e = {
        el,
        nameEl: el.querySelector("b")!,
        distEl: el.querySelector("i")!,
        arrowEl: el.querySelector(".pm-arrow")!,
        chevronEl: el.querySelector(".pm-chevron")!,
        alpha: 0,
        live: false,
        missingT: 0,
        lastDist: "",
        lastName: "",
        lastLane: null,
        lastEdgeSide: null,
        sx: 0,
        sy: 0,
        half: 0,
      };
      this.entries.set(key, e);
    }
    return e;
  }

  /**
   * @param origin floating-origin offset — the render frame the camera projects in.
   * @param from   the PLAYER's world position: what "3.4 km" on a tag has to mean.
   *   Distance used to be read straight off the render vector (pos − origin), but
   *   origin only rebases every ORIGIN_SNAP (30 km), so the number on the tag drifted
   *   away from the player by however far they had travelled since the last snap — a
   *   town 2 km away could read 21 km.
   */
  update(
    targets: BodyTarget[],
    origin: THREE.Vector3,
    camera: THREE.Camera,
    active: boolean,
    from: THREE.Vector3,
    dt: number,
  ): void {
    const W = window.innerWidth;
    const H = window.innerHeight;
    for (const e of this.entries.values()) e.live = false;
    this.slots.length = 0;

    if (active) {
      for (const t of targets) {
        const key = t.key ?? t.name;
        const e = this.entry(key);
        e.live = true;
        const dist = t.trueDist ?? t.pos.distanceTo(from);
        this.v.copy(t.pos).sub(origin);
        this.v.project(camera);
        const behind = this.v.z > 1;
        // Hysteresis on the screen↔edge decision: a tag already on screen only
        // leaves past a WIDER bound than an edge tag needs to come back in, so
        // a subject riding the old single threshold can't flap between forms.
        const horizonTarget = t.placement === "horizon";
        const wasScreen = e.lastLane === "screen" || e.lastLane === "horizon";
        // Horizon tags reserve a wider edge gutter. Without it, a label just
        // inside the viewport and an edge arrow just outside it were packed by
        // independent lanes and could overlap at the corners.
        const xBound = horizonTarget ? (wasScreen ? 0.78 : 0.70) : (wasScreen ? 0.95 : 0.89);
        const yBound = horizonTarget ? (wasScreen ? 0.80 : 0.72) : (wasScreen ? 0.91 : 0.85);
        const onscreen = !behind
          && Math.abs(this.v.x) < xBound
          && Math.abs(this.v.y) < yBound;
        const nm = t.name.toUpperCase();
        if (nm !== e.lastName) {
          e.lastName = nm;
          e.nameEl.textContent = nm;
          e.nameEl.style.color = t.color;
          // The on-screen chevron takes the subject's color too — the edge
          // arrow already did, and a tier-colored name over a green pointer
          // read as two different tags.
          e.chevronEl.style.color = t.color;
          e.half = 0; // a new name means the old sticky width is meaningless
        }
        // Granular distance (one decimal at km scale): the readout counts
        // smoothly at speed instead of ticking once a second, and the
        // string length stays constant so the centered box never shifts.
        const d =
          dist >= 1_000_000
            ? `${(dist / 1_000_000).toFixed(2)} Mm`
            : dist >= 1000
              ? `${(dist / 1000).toFixed(1)} km`
              : `${Math.round(dist)} m`;
        const txt = t.sub ? `${d} · ${t.sub}` : d;
        if (txt !== e.lastDist) {
          e.lastDist = txt;
          e.distEl.textContent = txt;
        }
        e.half = Math.max(e.half, halfWidth(nm, txt));
        const half = e.half;
        if (onscreen) {
          e.el.classList.remove("pm-edge");
          const horizon = horizonTarget;
          e.el.classList.toggle("pm-horizon", horizon);
          // Label centre sits half a block above the projected anchor. With
          // the chevron last in DOM order, its point now lands on that anchor.
          this.slots.push({
            e,
            x: ((this.v.x + 1) / 2) * W,
            y: ((1 - this.v.y) / 2) * H - SCREEN_H / 2,
            half,
            h: SCREEN_H,
            lane: horizon ? "horizon" : "screen",
            key,
          });
        } else {
          let nx = behind ? -this.v.x : this.v.x;
          let ny = behind ? -this.v.y : this.v.y;
          const m = Math.max(Math.abs(nx), Math.abs(ny), 1e-6);
          nx /= m;
          ny /= m;
          e.el.classList.add("pm-edge");
          e.el.classList.remove("pm-horizon");
          e.arrowEl.style.transform = `rotate(${Math.atan2(-ny, nx)}rad)`;
          e.arrowEl.style.color = t.color;
          const edgeSide: EdgeSide = Math.abs(ny) > Math.abs(nx)
            ? (ny > 0 ? "top" : "bottom")
            : (nx > 0 ? "right" : "left");
          this.slots.push({
            e,
            x: ((nx * 0.9 + 1) / 2) * W,
            y: ((1 - ny * 0.86) / 2) * H,
            half,
            h: EDGE_H,
            // The edge it is pinned against decides the axis it can slide along.
            lane: "edge",
            edgeSide,
            key,
          });
        }
      }
      // A removed/replaced target fades at its previous slot. Keep that fading
      // block in the solver until it is nearly transparent, otherwise a new
      // town immediately occupies the same pixels and the two names smear
      // together during the grace/fade window.
      for (const [key, e] of this.entries) {
        if (e.live || e.alpha <= 0.02 || e.lastLane === null) continue;
        this.slots.push({
          e,
          x: e.sx,
          y: e.sy,
          half: e.half,
          h: e.lastLane === "edge" ? EDGE_H : SCREEN_H,
          lane: e.lastLane,
          edgeSide: e.lastLane === "edge" ? e.lastEdgeSide ?? "left" : undefined,
          key,
        });
      }
      this.deoverlap(W, H);
      const slotEase = 1 - Math.exp(-SLOT_EASE_RATE * Math.max(0, Math.min(dt, 0.1)));
      // Displayed positions EASE toward the resolved slots: the de-overlap
      // shove becomes a glide. A tag that changed lanes (or is still fading
      // in) snaps instead — gliding across half the screen between an edge
      // and the centre would read worse than the jump it replaces.
      for (const s of this.slots) {
        const e = s.e;
        // Edge tags are constrained to a circular perimeter. Cartesian easing
        // between two perimeter slots cuts diagonally across the HUD and lets
        // packed labels pass through one another, so edge repacks commit as one
        // atomic layout. Horizon labels keep their gentle horizontal ease.
        if (s.lane === "edge" || e.lastLane !== s.lane || e.alpha < 0.05) {
          e.sx = s.x;
          e.sy = s.y;
        } else {
          e.sx += (s.x - e.sx) * slotEase;
          e.sy += (s.y - e.sy) * slotEase;
        }
        e.lastLane = s.lane;
        if (s.edgeSide) e.lastEdgeSide = s.edgeSide;
        e.el.dataset.markerLane = s.lane;
        e.el.style.transform = `translate(${e.sx}px, ${e.sy}px) translate(-50%, -50%)`;
      }
    }

    // Fades: ease toward presence; a short time-based grace window absorbs one-frame
    // flickers (occlusion ray grazing a horizon at speed) so tags never
    // blink; reap fully-faded stale entries.
    for (const [key, e] of this.entries) {
      e.missingT = e.live ? 0 : e.missingT + Math.max(0, Math.min(dt, 0.1));
      const want = e.live || e.missingT < MISSING_GRACE_S ? 0.85 : 0;
      const fade = 1 - Math.exp(-(want > e.alpha ? FADE_IN_RATE : FADE_OUT_RATE) * Math.max(0, Math.min(dt, 0.1)));
      e.alpha += (want - e.alpha) * fade;
      if (!e.live && e.missingT >= MISSING_GRACE_S && e.alpha < 0.02) {
        e.el.remove();
        this.entries.delete(key);
        continue;
      }
      e.el.style.opacity = String(e.alpha);
      e.el.style.display = e.alpha > 0.02 ? "flex" : "none";
    }
  }

  /**
   * Spread overlapping labels apart, so two towns on nearly the same bearing read as
   * two towns instead of one smear ("SNIONMOA SSHRIOSSING" — IRONSPIRE and DYXAL'S
   * BRIDGE drawn on the same pixels). Position only: every arrow still points at its
   * subject's true bearing, and the distances are untouched.
   *
   * Tags pinned to an edge are a ONE-DIMENSIONAL problem — they all share that edge's
   * coordinate — so each edge gets a sorted sweep that pushes each tag clear of its
   * predecessor, then a backward sweep if the run overflows the far end. That always
   * terminates. (The obvious "shove each tag off whatever it hits" does not: a tag
   * bounced clear of one label lands on the next and can oscillate back, which is how
   * the first attempt at this still left labels overlapping.)
   */
  private deoverlap(W: number, H: number): void {
    for (const lane of LANES) {
      this.lane.length = 0;
      for (const s of this.slots) if (s.lane === lane) this.lane.push(s);
      if (this.lane.length < 2) continue;
      if (lane === "edge") this.spreadEdge(this.lane, W, H);
      else if (lane === "horizon") {
        this.orderHorizon(this.lane);
        this.spread(this.lane, true, 0, W, false);
      }
      else this.stackScreen(this.lane, H);
    }
  }

  /**
   * Pack tags along one axis (x for a top/bottom edge, y for a side edge): sort, sweep
   * forward enforcing the gap, and if the run runs off the far end, sweep back from it.
   */
  private spread(lane: Slot[], alongX: boolean, min: number, max: number, sort = true): void {
    const pos = (s: Slot): number => (alongX ? s.x : s.y);
    const set = (s: Slot, v: number): void => { if (alongX) s.x = v; else s.y = v; };
    const size = (s: Slot): number => (alongX ? s.half : s.h / 2);
    // Quantized-position-then-key ordering: sorting on the raw float let
    // sub-pixel jitter swap two near-coincident tags every frame, snapping the
    // whole packed run back and forth. Genuine crossings still reorder.
    if (sort) lane.sort((a, b) => (Math.round(pos(a) / 8) - Math.round(pos(b) / 8)) || (a.key < b.key ? -1 : 1));
    for (let i = 1; i < lane.length; i++) {
      const need = pos(lane[i - 1]) + size(lane[i - 1]) + size(lane[i]);
      if (pos(lane[i]) < need) set(lane[i], need);
    }
    const last = lane[lane.length - 1];
    if (pos(last) + size(last) > max) {
      set(last, max - size(last));
      for (let i = lane.length - 2; i >= 0; i--) {
        const limit = pos(lane[i + 1]) - size(lane[i + 1]) - size(lane[i]);
        if (pos(lane[i]) > limit) set(lane[i], limit);
      }
    }
    for (const s of lane) set(s, Math.max(min + size(s), pos(s)));
  }

  /** Pack every edge tag on ONE continuous rectangular perimeter. Treating
   *  top/right/bottom/left as separate lanes made tags collide at corners and
   *  snap lanes on diagonal bearings. A perimeter coordinate makes both cases
   *  the same stable one-dimensional problem. */
  private spreadEdge(lane: Slot[], W: number, H: number): void {
    const x0 = W * 0.05, x1 = W * 0.95;
    const y0 = H * 0.07, y1 = H * 0.93;
    const w = x1 - x0, h = y1 - y0;
    const perimeter = 2 * (w + h);
    const at = (s: Slot): number => {
      switch (s.edgeSide) {
        case "top": return s.x - x0;
        case "right": return w + s.y - y0;
        case "bottom": return w + h + x1 - s.x;
        default: return 2 * w + h + y1 - s.y;
      }
    };
    // Along one side only one dimension matters; across a corner the perimeter
    // distance is |dx|+|dy|. `halfWidth + halfHeight` is the conservative
    // interval radius that guarantees at least one screen axis clears.
    const size = (s: Slot): number => s.half + s.h / 2;
    const raw = lane.map((s) => ({ s, p: at(s) })).sort((a, b) => a.p - b.p || (a.s.key < b.s.key ? -1 : 1));
    // Cut the circle at its largest empty arc, so the linear sweep cannot
    // push a run through a crowded corner.
    let cut = 0;
    let gap = -Infinity;
    for (let i = 0; i < raw.length; i++) {
      const next = i + 1 < raw.length ? raw[i + 1].p : raw[0].p + perimeter;
      const g = next - raw[i].p;
      if (g > gap) { gap = g; cut = (i + 1) % raw.length; }
    }
    const ordered = [...raw.slice(cut), ...raw.slice(0, cut)];
    const base = ordered[0].p;
    const pos = ordered.map((v, i) => v.p + (i > 0 && v.p < base ? perimeter : 0));
    for (let i = 1; i < ordered.length; i++) {
      pos[i] = Math.max(pos[i], pos[i - 1] + size(ordered[i - 1].s) + size(ordered[i].s));
    }
    const lastAllowed = base + perimeter - size(ordered[0].s) - size(ordered[ordered.length - 1].s);
    if (pos[pos.length - 1] > lastAllowed) {
      pos[pos.length - 1] = lastAllowed;
      for (let i = pos.length - 2; i > 0; i--) {
        pos[i] = Math.min(pos[i], pos[i + 1] - size(ordered[i + 1].s) - size(ordered[i].s));
      }
    }
    for (let i = 0; i < ordered.length; i++) {
      const slot = ordered[i].s;
      let p = ((pos[i] % perimeter) + perimeter) % perimeter;
      if (p <= w) { slot.x = x0 + p; slot.y = y0; slot.edgeSide = "top"; }
      else if ((p -= w) <= h) { slot.x = x1; slot.y = y0 + p; slot.edgeSide = "right"; }
      else if ((p -= h) <= w) { slot.x = x1 - p; slot.y = y1; slot.edgeSide = "bottom"; }
      else { p -= w; slot.x = x0; slot.y = y1 - p; slot.edgeSide = "left"; }
    }
  }

  /** Preserve last frame's horizon order, swapping adjacent labels only after
   *  their raw anchors have crossed by a meaningful amount. New labels insert
   *  at their natural screen position. */
  private orderHorizon(lane: Slot[]): void {
    const byKey = new Map(lane.map((s) => [s.key, s]));
    const ordered: Slot[] = [];
    for (const key of this.horizonOrder) {
      const s = byKey.get(key);
      if (s) {
        ordered.push(s);
        byKey.delete(key);
      }
    }
    for (const s of [...byKey.values()].sort((a, b) => a.x - b.x || (a.key < b.key ? -1 : 1))) {
      let i = ordered.findIndex((o) => o.x > s.x);
      if (i < 0) i = ordered.length;
      ordered.splice(i, 0, s);
    }
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i + 1 < ordered.length; i++) {
        if (ordered[i].x <= ordered[i + 1].x + HORIZON_ORDER_HYSTERESIS) continue;
        [ordered[i], ordered[i + 1]] = [ordered[i + 1], ordered[i]];
        changed = true;
      }
    }
    lane.splice(0, lane.length, ...ordered);
    this.horizonOrder = ordered.map((s) => s.key);
  }

  /** On-screen tags stack DOWNWARD off each other — but only against the ones they
   *  actually overlap horizontally (two labels far apart across the screen do not
   *  collide just because they share a row). Monotone, so it always settles. */
  private stackScreen(lane: Slot[], H: number): void {
    lane.sort((a, b) => (Math.round(a.y / 8) - Math.round(b.y / 8)) || (a.key < b.key ? -1 : 1));
    for (let i = 1; i < lane.length; i++) {
      const b = lane[i];
      for (let j = 0; j < i; j++) {
        const a = lane[j];
        if (Math.abs(b.x - a.x) >= a.half + b.half) continue;
        if (b.y < a.y + a.h) b.y = a.y + a.h;
      }
      b.y = Math.min(H - b.h / 2, b.y);
    }
  }
}

const LANES: Lane[] = ["edge", "horizon", "screen"];
